import { Component, OnInit, computed, effect, inject, input, model, viewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { FORM_COMPONENT_TOKEN, IFormComponent } from '@shared/components/forms/components/field-base/form-component.interface';
import { FormFieldDirective } from '@shared/components/forms/directives/form-field.directive';
import { FormsService } from '@shared/components/forms/services';
import { FieldTypes, IFieldConfig } from '@shared/models';

@Component({
  exportAs: 'appForm',
  selector: 'lib-form',
  styleUrls: ['form.component.scss'],
  templateUrl: 'form.component.html',
  imports: [FormsModule, ReactiveFormsModule, FormFieldDirective],
  providers: [
    { provide: FORM_COMPONENT_TOKEN, useExisting: AppFormComponent },
    FormsService,
  ],
})
export class AppFormComponent implements OnInit, IFormComponent {
  private readonly fb = inject(UntypedFormBuilder);

  public readonly ngForm = viewChild.required<NgForm>('formRef');
  public readonly model = model<unknown>();
  public readonly fullWidth = input<boolean>();
  public readonly config = model<IFieldConfig[]>([]);
  public form: UntypedFormGroup | undefined;

  public readonly filteredConfig = computed(() => {
    return (this.config() ?? []).filter(x => !x.hidden);
  });

  private readonly visibleConfig = computed(() => {
    return this.filteredConfig().filter(({ type, hidden }) => type !== FieldTypes.Button && type !== FieldTypes.ButtonGroup && !hidden);
  });

  public constructor() {
    effect(() => {
      this.configChanged();
    });
  }

  public ngOnInit() {
    this.form = this.createGroup();
    const model = this.model();
    if (model) {
      this.form?.patchValue(model);
    }
  }

  public configChanged() {
    if (this.form) {
      const controls = Object.keys(this.form?.controls);
      const visibleConfig = this.visibleConfig().map(item => item.name);

      controls.filter(control => !visibleConfig.includes(control)).forEach(control => this.form?.removeControl(control));

      visibleConfig
        .filter(control => !controls.includes(control))
        .forEach(name => {
          const config = this.filteredConfig().find(control => control.name === name);
          if (config) {
            this.form?.addControl(name, this.createControl(config));
          }
        });
    }
  }

  public get controls() {
    return this.form?.controls;
  }

  public get rawValue() {
    return this.form?.getRawValue();
  }

  public get changes() {
    return this.form?.valueChanges;
  }

  public get valid() {
    return this.form?.valid;
  }

  public get value() {
    return this.form?.value;
  }

  public get submitted() {
    return this.ngForm().submitted;
  }

  public createGroup() {
    const group = this.fb.group({});
    this.visibleConfig().forEach(control => group.addControl(control.name, this.createControl(control)));
    return group;
  }

  public createControl(config: IFieldConfig): AbstractControl {
    const { disabled, validation, asyncValidation, value, options } = config;
    if (config.type === FieldTypes.CheckboxList && options) {
      return this.fb.array(
        options.map(item => {
          return this.fb.control(item.selected);
        }),
        config.validation ? config.validation[0] : null,
      );
    }
    return this.fb.control({ disabled, value }, validation, asyncValidation);
  }

  public setDisabled(name: string, disable: boolean) {
    if (this.form?.controls[name]) {
      const method = disable ? 'disable' : 'enable';
      this.form?.controls[name][method]();
      return;
    }

    this.config().forEach(item => {
      if (item.name === name) {
        item.disabled = disable;
      }
    });
  }

  public setValue(name: string, value: unknown) {
    this.form?.controls[name].setValue(value, { emitEvent: true });
  }

  public patchForm(model: Record<string, any>) {
    this.form?.patchValue(model);
  }

  public reset() {
    this.ngForm().resetForm();
  }

  public validate() {
    if (!this.form) {
      return;
    }

    Object.keys(this.form.controls).forEach(field => {
      const control = this.form?.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  public showControl(name: string, show: boolean) {
    const fieldConfig = this.config().find(control => control.name === name);
    if (fieldConfig) {
      fieldConfig.hidden = !show;
    }

    if (show && fieldConfig) {
      this.form?.addControl(name, this.createControl(fieldConfig));
    } else {
      this.form?.removeControl(name);
    }

    this.config.set([...this.config()]);
  }
}
